import { AsyncPipe, formatDate, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { BudgetQuery } from '@models/budget/budget.query';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { ApiService, FileMetadata } from '@shared/services/api.service';
import { BudgetType, EntityType, EventType } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { batchPromises } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { WarningVendorEstimateComponent } from '../warning-vendor-estimate/warning-vendor-estimate.component';

Utils.extendDayjs();

type FileProcessingStatus = {
  destinationBucketKey: string;
  eventTrackerId?: string;
  fileId: string;
  originalBucketKey: string;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    WarningVendorEstimateComponent,
    NgSelectComponent,
    FormErrorDirective,
    FormsModule,
    ReactiveFormsModule,
    NgOptionComponent,
    InputComponent,
    FileManagerComponent,
    NgClass,
    IconComponent,
    AsyncPipe,
  ],
  selector: 'aux-quarter-close-add-upload',
  styles: [],
  templateUrl: './add-vendor-estimate-upload.component.html',
})
export class AddVendorEstimateUploadComponent {
  ref = inject(
    CustomOverlayRef<
      unknown,
      { notReturnFirstVendorByDefault?: boolean; selectedMonth: string; selectedVendor: string }
    >
  );
  vendorsQuery = inject(OrganizationQuery);

  private apiService = inject(ApiService);
  private budgetQuery = inject(BudgetQuery);
  private eventService = inject(EventService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  errorMessage = signal('');

  availableMonths$ = this.getAvailableMonths();
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  loading$ = new BehaviorSubject(false);
  metadata: FileMetadata = {};
  notes = new FormControl('');
  selectedMonth = new FormControl(this.availableMonths$[0].date);
  selectedVendor = new FormControl('');
  shortName = new FormControl('');
  showEstimateWarningBanner$ = this.launchDarklyService.select$(
    (flags) => flags.vendor_estimate_warn_banner
  );
  totalAmount = new FormControl('');

  private async cleanUpTempFiles(fileReferences: FileProcessingStatus[]) {
    if (!this.fileManager || !Array.isArray(fileReferences)) {
      return;
    }

    const fileRemovePromises = [];

    for (const fileRef of fileReferences) {
      this.fileManager.fileStore.update(fileRef.fileId, {
        bucket_key: fileRef.originalBucketKey,
      });

      if (!fileRef.eventTrackerId) {
        fileRemovePromises.push(this.apiService.removeFile(fileRef.destinationBucketKey));
      } else {
        this.fileManager.fileStore.remove(fileRef.fileId);
      }
    }

    const removalErrors: Error[] = [];

    try {
      (await batchPromises(fileRemovePromises, (p) => p)).forEach((r) => {
        if (r instanceof Error) {
          removalErrors.push(r);
        }
      });
    } catch (err) {
      removalErrors.push(err instanceof Error ? err : new Error(String(err)));
    }

    if (removalErrors.length) {
      console.error('Failed to remove some of the temp files', removalErrors);
    }
  }

  constructor() {
    if (this.ref.data) {
      const { selectedMonth, selectedVendor } = this.ref.data;

      this.selectedVendor = new FormControl(selectedVendor || '');
      this.selectedMonth = new FormControl(selectedMonth || this.availableMonths$[0].date);
    }
  }

  getAvailableMonths() {
    const { budget_info } = this.budgetQuery.getValue();
    let date = dayjs(budget_info[0].current_month);
    const months = [];
    const current_quarter = date.quarter();

    while (date.quarter() === current_quarter) {
      months.push({ date: date.format('MM-DD-YYYY'), name: date.format('MMMM YYYY') });
      date = date.add(1, 'months');
    }

    return months;
  }

  getFilePath(month: string) {
    // const date = formatDate(new Date(), 'MMM-YYYY', 'en-US');
    const formatted_current_month = formatDate(month, 'MMMM-y', 'en-US');
    const trialId = this.mainQuery.getValue().trialKey;

    // vendor/<vendor uuid>/in-month/<MON-YYYY>/<file name goes here>
    return `trials/${trialId}/vendors/vendor-estimate/${formatted_current_month}/`;
  }

  async onLinkClick() {
    const vendorList = await firstValueFrom(this.vendorsQuery.allVendors$);
    const vendorId = this.selectedVendor.value ? this.selectedVendor.value : vendorList[0].id;

    const goToEditModeWithCurrentFilters = !this.selectedVendor.value || !this.selectedMonth.value;

    if (goToEditModeWithCurrentFilters) {
      this.ref.close({
        goToEditModeWithCurrentFilters: true,
      });

      return;
    }

    this.ref.close({
      month: this.selectedMonth.value,
      vendorId,
    });
  }

  // eslint-disable-next-line max-lines-per-function
  async onUpload() {
    if (!this.fileManager || this.loading$.getValue()) {
      return;
    }

    this.selectedVendor.markAsTouched();
    this.selectedVendor.updateValueAndValidity();

    if (this.selectedVendor.invalid) {
      return;
    }

    this.errorMessage.set('');

    const { budget_info } = this.budgetQuery.getValue();
    const date = this.selectedMonth.value || budget_info[0].current_month;

    if (!date) {
      this.errorMessage.set('No in month found!');

      return;
    }

    const files = this.fileManager.fileQuery.getAll();

    if (!files.length) {
      this.errorMessage.set('You need to upload a file!');

      return;
    }

    let processingFailed = false;
    const fileStatuses: FileProcessingStatus[] = [];

    this.loading$.next(true);

    try {
      const filePath = this.getFilePath(date);

      for (const file of files) {
        const fileStatus: FileProcessingStatus = {
          destinationBucketKey: `${filePath}${file.bucket_key}`,
          fileId: file.id,
          originalBucketKey: file.bucket_key,
        };

        fileStatuses.push(fileStatus);
        this.fileManager.fileStore.update(file.id, {
          bucket_key: fileStatus.destinationBucketKey,
          uploaded: false,
        });
      }

      const metadata: FileMetadata = { admin: '1' };

      if (!(await this.fileManager.fileService.uploadFiles({ metadata: metadata }))) {
        processingFailed = true;

        return;
      }

      const eventSubmitCommands = fileStatuses.map((fileProcessingStatus) => async () => {
        const result = await firstValueFrom(
          this.eventService.processEvent$({
            bucket_key: `public/${fileProcessingStatus.destinationBucketKey}`,
            entity_id: this.selectedVendor.value || '',
            entity_type: EntityType.ORGANIZATION,
            payload: JSON.stringify({
              amount: this.totalAmount.value,
              budget_type: BudgetType.BUDGET_VENDOR_ESTIMATE,
              month: this.selectedMonth.value,
              notes: this.notes.value,
              short_name: this.shortName.value,
            }),
            type: EventType.VENDOR_ESTIMATE_SUPPORTING_DOCUMENT_UPLOADED,
          })
        );

        if (result.success && result.data?.id) {
          fileProcessingStatus.eventTrackerId = result.data.id;

          return;
        }

        throw new Error(result.errors[0] || 'An error occurred');
      });
      const eventSubmitPromises = eventSubmitCommands.map((cmd) => cmd());
      const eventSubmitResults = await batchPromises(eventSubmitPromises, (p) => p);

      if (eventSubmitResults.some((r) => r instanceof Error)) {
        processingFailed = true;
        console.error('Some events failed', eventSubmitResults);
      } else {
        this.overlayService.success();
        this.ref.close(true);
      }
    } catch (err) {
      processingFailed = true;
      console.error('Failed to process files', err);
    } finally {
      if (processingFailed) {
        await this.cleanUpTempFiles(fileStatuses);
        this.overlayService.error('Error uploading files.');
      }

      this.loading$.next(false);
    }
  }

  pathFn: () => string = () => '';

  removeFile(file: File) {
    if (this.fileManager) {
      void this.fileManager.removeFile(file);
    }
  }
}
